import findResponsiveItem from 'presentation/utils/findResponsiveItem';
import { ResponsiveSet } from 'core/ribbons/primitives';
import { Layout } from 'domain/constants/layout';
import { BreakpointProps } from '@ux/responsive';
import { CTA } from 'core/ribbons/cta';
import { ShortcodeProps } from 'presentation/components/Shortcode';
import { css, cx } from 'linaria';
import { cssTheme } from '@uds/theme-utils';
import { classNames } from '@uds/typography';
import { styled } from 'linaria/lib/react';
import { isNumeric } from '@ux/js';

const ctas = {
  default: css`
    margin-top: ${cssTheme.gu3};
    flex-wrap: wrap;
    &&& {
      ${cssTheme.breakpoints.sm} {
        display: flex;
      }
    }
  `,
  multiple: css`
    margin-top: ${cssTheme.gu2};
  `,
};

const action = css`
  margin-top: ${cssTheme.gu1};
  &&& {
    ${cssTheme.breakpoints.sm} {
      margin-right: ${cssTheme.gu1};
    }
  }
`;

const txt = css`
  margin-top: ${cssTheme.gu3};
`;

const cntnt = {
  default: css`
    // 2GU between text and image (2 + 1 from Column)
    &&& {
      ${cssTheme.breakpoints.sm} {
        margin-right: ${cssTheme.gu1};
      }
    }
    // 3GU between text and image (2 + 1 from Column)
    &&& {
      ${cssTheme.breakpoints.lg} {
        margin-right: ${cssTheme.gu2};
      }
    }
  `,
  flipped: css`
    // 2GU between text and image (2 + 1 from Column)
    &&& {
      ${cssTheme.breakpoints.sm} {
        margin-right: 0;
        margin-left: ${cssTheme.gu1};
      }
    }
    // 3GU between text and image (2 + 1 from Column)
    &&& {
      ${cssTheme.breakpoints.lg} {
        margin-right: 0;
        margin-left: ${cssTheme.gu2};
      }
    },
  `,
};

const root = css`
  position: relative;
  flex: 1;
  flex-basis: auto;
  padding-bottom: ${cssTheme.gu4};
  &&& {
    ${cssTheme.breakpoints.sm} {
      height: 100%;
    }
  }
`;

interface RootProps {
  height?: ResponsiveSet<string | number>;
  layout: Layout;
}

const getHeights = ({
  height = ['auto', 'auto', 'auto'],
  atBreakpoint = 'xs',
}: {
  height?: ResponsiveSet<string | number>;
  atBreakpoint?: string;
}) => {
  const breakpoints = {
    isMinSm: ['xl', 'lg', 'md', 'sm'].includes(atBreakpoint),
    isMinMd: ['xl', 'lg', 'md'].includes(atBreakpoint),
    isMinLg: ['xl', 'lg'].includes(atBreakpoint),
    isMinXl: atBreakpoint === 'xl' ?? false,
  } as BreakpointProps;
  const getActiveFromArray = (array: ResponsiveSet<string | number>) => findResponsiveItem(breakpoints, array);

  const activeHeightInitial = getActiveFromArray(height);
  const activeHeight = isNumeric(activeHeightInitial) ? `${activeHeightInitial}px` : activeHeightInitial;
  const transitionHeight = activeHeight === 'auto' ? '200px' : activeHeight;
  return {
    activeHeight,
    transitionHeight,
  };
};

const heightAnchor = css`
  ${cssTheme.breakpoints.sm} {
    display: flex;
    height: 100%;
  }
`;

export const StyledRoot = styled.div<RootProps>`
  min-height: ${(props) => getHeights({ height: props?.layout?.height }).transitionHeight};
  ${cssTheme.breakpoints.sm} {
    min-height: ${(props) => getHeights({ height: props?.layout?.height, atBreakpoint: 'sm' }).transitionHeight};
    padding-top: calc(var(--centerfold-padding-top) * ${cssTheme.gu1});
    padding-bottom: calc(var(--centerfold-padding-bottom) * ${cssTheme.gu1});
    & .${heightAnchor} {
      min-height: ${(props) => getHeights({ height: props?.layout?.height, atBreakpoint: 'sm' }).activeHeight};
    }
  }
  ${cssTheme.breakpoints.md} {
    min-height: ${(props) => getHeights({ height: props?.layout?.height, atBreakpoint: 'md' }).transitionHeight};
  }
  ${cssTheme.breakpoints.lg} {
    min-height: ${(props) => getHeights({ height: props?.layout?.height, atBreakpoint: 'lg' }).transitionHeight};
  }
`;

export const getClassNames = ({
  text,
  layout,
}: {
  text: {
    title: ShortcodeProps;
    body: ShortcodeProps;
    ctas: Array<CTA>;
  };
  layout: Layout;
}) => {
  const multipleCtas = Array.isArray(text.ctas) && text.ctas.length > 1;
  const { flipped } = layout;
  return {
    root,
    ctas: cx(ctas.default, multipleCtas && ctas.multiple),
    action: cx(multipleCtas && action),
    text: cx(txt, classNames.content),
    heightAnchor,
    content: cx(cntnt.default, flipped && cntnt.flipped),
  };
};
